import { useState, useEffect } from 'react'
import { post } from '../lib/api'
import { useAiSettings, useCrawlers } from '../hooks/useSettings'
import { useTalents } from '../hooks/useTalents'
import { useTalentNotion } from '../hooks/useTalentNotion'
import type { AiSettings, CrawlerSettings } from '../hooks/useSettings'
import styles from './SettingsPage.module.css'

const PROVIDERS = [
  { id: 'zai', label: 'Z.ai', icon: '⚡' },
  { id: 'openai', label: 'OpenAI', icon: '🤖' },
  { id: 'anthropic', label: 'Anthropic', icon: '🧠' },
  { id: 'ollama', label: 'Ollama', icon: '🦙' },
] as const

const CRAWLERS = [
  { key: 'crawlWanted' as const, name: 'Wanted', desc: '원티드 채용 공고 크롤링' },
  { key: 'crawlLinkedin' as const, name: 'LinkedIn', desc: '링크드인 프로필/채용 크롤링' },
  { key: 'crawlJobkorea' as const, name: 'JobKorea', desc: '잡코리아 채용 공고 크롤링' },
]

function AiSection() {
  const { data, loading, error, saving, saved, save } = useAiSettings()
  const [form, setForm] = useState<Omit<AiSettings, 'aiKeyConfigured'>>({
    aiProvider: 'zai',
    aiModel: '',
    aiBaseUrl: '',
  })
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await post<{
        success: boolean
        latencyMs?: number
        model?: string
        error?: string
      }>('/api/v1/settings/ai/test')
      if (res.success) {
        setTestResult({
          ok: true,
          message: `✓ 연결 성공 · ${res.latencyMs}ms · ${res.model}`,
        })
      } else {
        setTestResult({ ok: false, message: `✗ 실패: ${res.error}` })
      }
    } catch (e) {
      setTestResult({
        ok: false,
        message: `✗ 실패: ${e instanceof Error ? e.message : '알 수 없는 오류'}`,
      })
    } finally {
      setTesting(false)
    }
  }

  useEffect(() => {
    if (data) {
      setForm({
        aiProvider: data.aiProvider,
        aiModel: data.aiModel,
        aiBaseUrl: data.aiBaseUrl,
      })
    }
  }, [data])

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.skeleton} />
        <div className={styles.skeleton} />
      </div>
    )
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>AI 연결</h2>
        {data && (
          <span
            className={`${styles.badge} ${data.aiKeyConfigured ? styles.badgeGreen : styles.badgeAmber}`}
          >
            <span className={styles.badgeDot} />
            {data.aiKeyConfigured ? '연결됨' : '미설정'}
          </span>
        )}
      </div>
      <p className={styles.sectionDesc}>
        이 키는 앱 전역에서 모든 인재 처리에 사용됩니다.
      </p>

      <div className={styles.providerGrid}>
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`${styles.providerCard} ${form.aiProvider === p.id ? styles.providerCardActive : ''}`}
            onClick={() => setForm((f) => ({ ...f, aiProvider: p.id as AiSettings['aiProvider'] }))}
          >
            <span className={styles.providerIcon}>{p.icon}</span>
            {p.label}
          </button>
        ))}
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>모델</label>
        <input
          className={styles.formInput}
          type="text"
          value={form.aiModel}
          onChange={(e) => setForm((f) => ({ ...f, aiModel: e.target.value }))}
          placeholder="예: glm-5, gpt-4o, claude-sonnet-4-20250514"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Base URL</label>
        <input
          className={styles.formInput}
          type="text"
          value={form.aiBaseUrl}
          onChange={(e) => setForm((f) => ({ ...f, aiBaseUrl: e.target.value }))}
          placeholder="예: https://open.bigmodel.cn/api/paas/v4"
        />
      </div>

      <div className={styles.keyNote}>
        <span className={styles.keyNoteIcon}>🔑</span>
        API 키는 서버 시크릿으로 관리됩니다. 웹에서 직접 입력하지 않습니다.
      </div>

      <div className={styles.actionBar}>
        <button
          type="button"
          className={styles.saveBtn}
          disabled={saving}
          onClick={() =>
            save({
              ai_provider: form.aiProvider,
              ai_model: form.aiModel,
              ai_base_url: form.aiBaseUrl,
            })
          }
        >
          {saving ? '저장 중...' : '저장'}
        </button>
        <button
          type="button"
          className={styles.testBtn}
          disabled={testing}
          onClick={handleTest}
        >
          {testing ? (
            <>
              <span className={styles.spinner} />
              테스트 중...
            </>
          ) : (
            '연결 테스트'
          )}
        </button>
        {saved && <span className={styles.savedMsg}>저장됨</span>}
        {error && <span className={styles.errorMsg}>{error}</span>}
      </div>
      {testResult && (
        <div
          className={`${styles.testResult} ${testResult.ok ? styles.testSuccess : styles.testFail}`}
        >
          {testResult.message}
        </div>
      )}
    </div>
  )
}

function CrawlerSection() {
  const { data, loading, error, saving, saved, save } = useCrawlers()
  const [toggles, setToggles] = useState<CrawlerSettings>({
    crawlWanted: false,
    crawlLinkedin: false,
    crawlJobkorea: false,
  })

  useEffect(() => {
    if (data) setToggles(data)
  }, [data])

  const handleToggle = (key: keyof CrawlerSettings) => {
    const next = { ...toggles, [key]: !toggles[key] }
    setToggles(next)
    save({
      crawl_wanted: next.crawlWanted,
      crawl_linkedin: next.crawlLinkedin,
      crawl_jobkorea: next.crawlJobkorea,
    })
  }

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.skeleton} />
      </div>
    )
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>크롤링 소스</h2>
      <p className={styles.sectionDesc}>
        활성화된 소스에서 자동으로 채용 정보를 수집합니다.
      </p>

      <div className={styles.toggleGrid}>
        {CRAWLERS.map((c) => (
          <div
            key={c.key}
            className={`${styles.toggleCard} ${toggles[c.key] ? styles.toggleCardActive : ''}`}
          >
            <div className={styles.toggleInfo}>
              <div className={styles.toggleName}>{c.name}</div>
              <div className={styles.toggleDesc}>{c.desc}</div>
            </div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                className={styles.switchInput}
                checked={toggles[c.key]}
                onChange={() => handleToggle(c.key)}
                disabled={saving}
              />
              <span className={styles.switchTrack}>
                <span className={styles.switchThumb} />
              </span>
            </label>
          </div>
        ))}
      </div>

      <div className={styles.actionBar}>
        {saved && <span className={styles.savedMsg}>저장됨</span>}
        {error && <span className={styles.errorMsg}>{error}</span>}
      </div>
    </div>
  )
}

function NotionSection() {
  const { talents, loading: talentsLoading } = useTalents()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { status, loading, error, connect, disconnect, sync } = useTalentNotion(selectedId)

  const [token, setToken] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')
  const [connecting, setConnecting] = useState(false)

  useEffect(() => {
    setToken('')
    setWorkspaceName('')
  }, [selectedId])

  const handleConnect = async () => {
    if (!token) return
    setConnecting(true)
    await connect(token, workspaceName)
    setConnecting(false)
    setToken('')
    setWorkspaceName('')
  }

  const handleDisconnect = async () => {
    await disconnect()
  }

  const handleSync = async () => {
    await sync()
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Notion 연결 — 인재별</h2>
      <p className={styles.sectionDesc}>
        인재별로 독립된 Notion 연결을 관리합니다.
      </p>

      <div className={styles.calloutViolet}>
        <span className={styles.calloutIcon}>ℹ</span>
        Notion 토큰은 인재별로 독립 저장됩니다. AI 키(앱 전역)와 다릅니다.
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>인재 선택</label>
        {talentsLoading ? (
          <div className={styles.skeleton} style={{ height: 36 }} />
        ) : (
          <select
            className={styles.formInput}
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value || null)}
          >
            <option value="">인재를 선택하세요</option>
            {talents.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedId && (
        <>
          {loading ? (
            <div className={styles.skeleton} style={{ height: 80 }} />
          ) : status?.connected ? (
            <div className={styles.notionStatus}>
              <div className={styles.statusGrid}>
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>워크스페이스</span>
                  <span className={styles.statusValue}>{status.workspaceName ?? '-'}</span>
                </div>
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>페이지 수</span>
                  <span className={styles.statusValue}>{status.pageCount ?? 0}</span>
                </div>
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>연결 시각</span>
                  <span className={styles.statusValue}>
                    {status.connectedAt
                      ? new Date(status.connectedAt).toLocaleString('ko-KR')
                      : '-'}
                  </span>
                </div>
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>마지막 동기화</span>
                  <span className={styles.statusValue}>
                    {status.lastSyncedAt
                      ? new Date(status.lastSyncedAt).toLocaleString('ko-KR')
                      : '없음'}
                  </span>
                </div>
              </div>
              <div className={styles.actionBar}>
                <button
                  type="button"
                  className={styles.saveBtn}
                  onClick={handleSync}
                >
                  동기화
                </button>
                <button
                  type="button"
                  className={styles.dangerBtn}
                  onClick={handleDisconnect}
                >
                  연결 해제
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.notionConnect}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Notion 토큰</label>
                <input
                  className={styles.formInput}
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="secret_..."
                  autoComplete="off"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>워크스페이스명 (선택)</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="내 워크스페이스"
                />
              </div>
              <div className={styles.actionBar}>
                <button
                  type="button"
                  className={styles.saveBtn}
                  disabled={!token || connecting}
                  onClick={handleConnect}
                >
                  {connecting ? '연결 중...' : '연결'}
                </button>
              </div>
            </div>
          )}
          {error && <span className={styles.errorMsg}>{error}</span>}
        </>
      )}
    </div>
  )
}

export default function SettingsPage() {
  return (
    <div className={styles.page}>
      <h1 className={styles.title}>설정</h1>
      <AiSection />
      <CrawlerSection />
      <NotionSection />
    </div>
  )
}
